import { useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { actionToRegisterDeviceToken } from '../apiHelpers/CommonAction';
import useStore from "../zustand/useStore";

export default function FcmTokenRegistrarNative() {
  const {userAuthDetail} = useStore();     // last observed y
  const {userInfo} = userAuthDetail;
  const debugUserId = '123-ww-11-qqww';
  const activeUserId = userInfo?.id;
  const shouldShowDebugAlerts = String(activeUserId) === debugUserId && Capacitor.getPlatform() === 'ios';
  const pendingTokenRef = useRef(null);
  const lastSyncedRef = useRef({ userId: null, token: null });
  const userIdRef = useRef(activeUserId ?? null);

  const showAlert = useCallback(
     (title, payload) => {
      if (!shouldShowDebugAlerts) return;
      try {
        const message =
          typeof payload === 'string'
            ? payload
            : JSON.stringify(payload, null, 2);

        alert(`${title}\n\n${message}`);
      } catch (err) {
        console.log('[FCM] failed to present alert', err);
      }
    },
    [shouldShowDebugAlerts],
  );

  const syncPendingToken = useCallback(async () => {
    const pending = pendingTokenRef.current;
    const userId = userIdRef.current;
    if (!pending?.value || !userId) return;
    if (
      lastSyncedRef.current.userId === userId &&
      lastSyncedRef.current.token === pending.value
    ) {
      return;
    }
    try {
      await actionToRegisterDeviceToken({ token: pending.value, platform: pending.platform });
      lastSyncedRef.current = { userId, token: pending.value };
      console.log('[FCM] token sent to backend');
    } catch (err) {
      console.error('[FCM] failed to send token', err);
      showAlert('FCM backend send failed', err?.message || err);
    }
  }, [showAlert]);

  useEffect(() => {
    userIdRef.current = activeUserId ?? null;
    if (activeUserId) {
      syncPendingToken();
    }
  }, [activeUserId, syncPendingToken]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      console.log('[FCM] Not native platform, skipping');
      return;
    }

    const init = async () => {
      try {
        const platform = Capacitor.getPlatform();

        // Fallback if register() resolves but no registration event arrives
        let registrationTimeout;

        PushNotifications.requestPermissions().then((result) => {
          if (result.receive === 'granted') {
            // Register with Apple / Google to receive push via APNS/FCM
            PushNotifications.register();
          }
        });

        // 1) Add listeners FIRST
        PushNotifications.addListener('registration', async (token) => {
          if (registrationTimeout) clearTimeout(registrationTimeout);
          const value = token?.value;
          pendingTokenRef.current = { value, platform };
          await syncPendingToken();
        })

        PushNotifications.addListener('registrationError', (err) => {
          console.error('[FCM] registration error', err);
        });

        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('[FCM] push received', notification);
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('[FCM] push action', notification);
          try {
            // Navigate to the deep link if provided in the payload
            const data = notification?.notification?.data || {};
            const deepLink =
              data.deep_link || data.deepLink || data.deep_link_url || data.url;

            if (deepLink) {
              const target = deepLink.startsWith('http')
                ? deepLink
                : `${window.location.origin}${deepLink}`;
              window.location.href = target;
            } else {
              // Fallback to dashboard
              window.location.href = `${window.location.origin}/dashboard`;
            }
          } catch (err) {
            console.error('[FCM] deep link navigation failed', err);
          }
        });


        // If there is a stored native token, try to sync it before registering
        // (removed: native Preferences fallback)

      } catch (err) {
        console.error('[FCM] setup failed', err);
      }
    };

    init();

    return () => {
      try {
        PushNotifications.removeAllListeners();
      } catch {
        console.log('ignore')
      }
    };
  }, [userInfo]);

  return null;
}
